import type {
  AddBasicPhysicalPersonDataRequest,
  AddBasicPhysicalPersonDataResponse,
  AddEmailRequest,
  AddEmailResponse,
  AlterPasswordRequest,
  AlterPasswordResponse,
  ContactLookupRequest,
  GetLegalEntityByCnpjResponse,
  GetPhysicalPersonByCpfResponse,
  IncluirRelacionamentoNegocioRequest,
  IncluirRelacionamentoNegocioResponse,
  IRegisterPolicyholderService,
  LinkPersonToBusinessRepresentationRequest,
  LinkPersonToBusinessRepresentationResponse,
  ListFilteredBusinessLinksParams,
  UserPoliciesListGroup,
  UserPoliciesListParams,
  UserPoliciesListResponse,
  ListBusinessLinkFiltersResponse,
} from '../../types/serviceType';
import type { GetContactsResponse } from '../../types/apiType';
import { REGISTER_POLICYHOLDER_ROUTES } from '../requestsService';

export class RegisterPolicyholderService implements IRegisterPolicyholderService {
  private fetch: <T>(endpoint: string, options?: RequestInit) => Promise<T>;

  constructor(fetch: <T>(endpoint: string, options?: RequestInit) => Promise<T>) {
    this.fetch = fetch;
  }

  /**
   * Adds basic physical person data (POST /cadp/incluir-dados-basicos-pessoa-fisica)
   */
  async postAddBasicPhysicalPersonData(data: AddBasicPhysicalPersonDataRequest): Promise<AddBasicPhysicalPersonDataResponse> {
    return this.fetch<AddBasicPhysicalPersonDataResponse>(REGISTER_POLICYHOLDER_ROUTES.POST_ADD_BASIC_PHYSICAL_PERSON_DATA, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Gets legal entity by CNPJ (POST /cadp/recuperar-pessoa-juridica?cnpj=)
   */
  async postGetLegalEntityByCnpj(cnpj: string): Promise<GetLegalEntityByCnpjResponse> {
    const url = `${REGISTER_POLICYHOLDER_ROUTES.POST_GET_LEGAL_ENTITY_BY_CNPJ}?cnpj=${encodeURIComponent(cnpj)}`;
    return this.fetch<GetLegalEntityByCnpjResponse>(url, {
      method: 'POST',
    });
  }

  /**
   * Gets physical person by CPF (POST /cadp/recuperar-pessoa-fisica?cpf=)
   */
  async postGetPhysicalPersonByCpf(cpf: string): Promise<GetPhysicalPersonByCpfResponse> {
    const url = `${REGISTER_POLICYHOLDER_ROUTES.POST_GET_PHYSICAL_PERSON_BY_CPF}?cpf=${encodeURIComponent(cpf)}`;
    return this.fetch<GetPhysicalPersonByCpfResponse>(url, {
      method: 'POST',
    });
  }

  /**
   * Inclui relacionamento de negócio / representação (POST /cadp/incluir-relacionamento-negocio)
   */
  async postIncluirRelacionamentoNegocio(data: IncluirRelacionamentoNegocioRequest): Promise<IncluirRelacionamentoNegocioResponse> {
    return this.fetch<IncluirRelacionamentoNegocioResponse>(REGISTER_POLICYHOLDER_ROUTES.POST_INCLUIR_RELACIONAMENTO_NEGOCIO, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Links person to business representation (POST /cadp/vincular-pessoa-representacao-negocio)
   */
  async postLinkPersonToBusinessRepresentation(data: LinkPersonToBusinessRepresentationRequest): Promise<LinkPersonToBusinessRepresentationResponse> {
    return this.fetch<LinkPersonToBusinessRepresentationResponse>(REGISTER_POLICYHOLDER_ROUTES.POST_LINK_PERSON_TO_BUSINESS_REPRESENTATION, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Lists filtered business links (POST /cadp/listar-vinculo-negocio/filtrados) — (idPessoa + produtoIds e/ou chaveLegado).
   */
  async postListFilteredBusinessLinks(params: ListFilteredBusinessLinksParams): Promise<ListBusinessLinkFiltersResponse> {
    const search = new URLSearchParams();
    search.set('idPessoa', String(params.idPessoa));
    if (params.produtoIds != null) {
      search.set('produtoIds', String(params.produtoIds));
    }
    if (params.chaveLegado != null && params.chaveLegado !== '') {
      search.set('chaveLegado', params.chaveLegado);
    }
    const url = `${REGISTER_POLICYHOLDER_ROUTES.POST_LIST_BUSINESS_LINK_FILTERS}?${search.toString()}`;
    return this.fetch<ListBusinessLinkFiltersResponse>(url, {
      method: 'POST',
    });
  }

  /**
   * Adds email to person (POST /cadp/adicionar-email)
   */
  async postAddEmail(data: AddEmailRequest): Promise<AddEmailResponse> {
    await this.fetch<unknown>(REGISTER_POLICYHOLDER_ROUTES.POST_ADD_EMAIL, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * changes password GIBS (POST /gibs/alterar-senha)
   */
  async postAlterPassword(data: AlterPasswordRequest): Promise<AlterPasswordResponse> {
    await this.fetch<unknown>(REGISTER_POLICYHOLDER_ROUTES.POST_ALTER_PASSWORD, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Looks up contacts (POST /cadp/consultar-contato)
   */
  async postContactLookup(data: ContactLookupRequest): Promise<GetContactsResponse> {
    return this.fetch<GetContactsResponse>(REGISTER_POLICYHOLDER_ROUTES.POST_LIST_CONTACTS, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Lists policies for user/CNPJ (GET /vida/apolice/listar-apol-perf-usuario)
   */
  async getUserPoliciesList(params: UserPoliciesListParams): Promise<UserPoliciesListResponse> {
    const search = new URLSearchParams();
    if (params.numApoliceApol != null) {
      search.set('numApoliceApol', String(params.numApoliceApol));
    }
    if (params.numDocumentoApol != null && params.numDocumentoApol !== '') {
      search.set('numDocumentoApol', params.numDocumentoApol.replace(/\D/g, ''));
    }
    const url = `${REGISTER_POLICYHOLDER_ROUTES.GET_LIST_POLICIES_FOR_USER}?${search.toString()}`;
    const raw = await this.fetch<UserPoliciesListResponse | UserPoliciesListGroup>(url, { method: 'GET' });
    if (Array.isArray(raw)) {
      return raw;
    }
    return [raw];
  }
}
